<template>
  <div class="event-layout">
    <EventSidebar :event-id="eventId" />

    <main class="event-main">
      <div class="page-header">
        <div>
          <p class="breadcrumb">Events → {{ eventName }}</p>
          <h1 class="page-title">Queue</h1>
          <p class="page-subtitle">
            Presenter works from preview. Lower thirds only change when the preview item is pushed live.
          </p>
        </div>
      </div>

      <div v-if="error" class="notice error">{{ error }}</div>
      <div v-if="successMessage" class="notice success">{{ successMessage }}</div>

      <QueueSyncStatus :state="syncState" />
      <fieldset :disabled="!canOperate" class="queue-operation-controls">
      <section class="panel mb-6">
        <div class="controls-row">
          <div>
            <h2 class="panel-title">Live State</h2>
            <p class="panel-copy">
              {{ localItems.length }} active items, {{ history.length }} used
            </p>
            <p v-if="queuePublishedAt" class="published-copy">Published {{ formattedPublishedAt }}</p>
          </div>

        </div>
      </section>

      <section v-if="!localItems.length" class="panel empty-panel">
        <p>No live queue has been published yet.</p>
        <router-link :to="participantsPath" class="primary-link mt-4">
          Go to Participants
        </router-link>
      </section>

      <template v-else>
        <section class="panel mb-6">
          <h2 class="panel-title">Late Add From Roster</h2>
          <p class="panel-copy">Search the locked participant roster and append a late student to the bottom of the upcoming queue.</p>

          <div class="mt-4">
            <input
              v-model.trim="lateAddSearchTerm"
              type="text"
              class="field-input"
              placeholder="Search by student name..."
            />
          </div>

          <div v-if="lateAddSearchTerm" class="late-add-results mt-6">
            <div v-if="!lateAddSearchResults.length" class="empty-state compact">
              No student roster entries match this search.
            </div>

            <div v-else class="late-add-list">
              <div
                v-for="result in lateAddSearchResults"
                :key="`late-add-${result.participant.id}`"
                class="late-add-card"
              >
                <div class="queue-card-header">
                  <div class="queue-copy">
                    <p class="queue-headline">{{ participantDisplayName(result.participant) }}</p>
                    <p v-if="itemSubline(result.participant)" class="queue-subline">{{ itemSubline(result.participant) }}</p>
                    <p v-if="itemPhonetics(result.participant)" class="queue-phonetics">Orator notes / phonetics: {{ itemPhonetics(result.participant) }}</p>
                  </div>
                  <div class="queue-card-badges">
                    <span v-if="seatNumberPillText(result.participant)" class="seat-number-pill">
                      {{ seatNumberPillText(result.participant) }}
                    </span>
                    <span class="late-add-status" :class="{ warning: result.isDuplicate, clean: !result.isDuplicate }">
                      {{ lateAddStatusLabel(result.duplicateContext) }}
                    </span>
                  </div>
                </div>

                <p v-if="result.isDuplicate" class="late-add-warning">
                  {{ lateAddWarningCopy(result.duplicateContext) }}
                </p>

                <div class="card-actions">
                  <button
                    class="primary-button"
                    :disabled="addingParticipantId === result.participant.id"
                    @click="addRosterParticipant(result.participant.id, result.isDuplicate)"
                  >
                    {{ addingParticipantId === result.participant.id ? "Adding..." : lateAddActionLabel(result.duplicateContext) }}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section class="state-grid mb-6">
          <article class="panel state-card">
            <div class="state-card-header">
              <div>
                <p class="state-label">Next Up</p>
                <h2 class="panel-title">Presenter</h2>
              </div>
              <div v-if="previewItem" class="queue-card-badges">
                <span v-if="seatNumberPillText(previewItem)" class="seat-number-pill">
                  {{ seatNumberPillText(previewItem) }}
                </span>
                <span class="status-pill preview">Locked</span>
              </div>
            </div>

            <div v-if="previewItem" class="state-body">
              <p class="state-title">{{ itemHeadline(previewItem) }}</p>
              <p v-if="itemSubline(previewItem)" class="state-subline">{{ itemSubline(previewItem) }}</p>
              <p v-if="itemPhonetics(previewItem)" class="queue-phonetics">Orator notes / phonetics: {{ itemPhonetics(previewItem) }}</p>
              <p class="state-meta">Position {{ previewIndex + 1 }} in the active queue.</p>
            </div>
            <div v-else class="empty-state">
              There is no preview item set.
            </div>

            <div class="card-actions state-actions">
              <button
                class="secondary-button"
                :disabled="!canUndoAnnounce"
                @click="undoLastAnnounce"
              >
                Undo Last Announce
              </button>
              <button
                class="primary-button"
                :disabled="!previewItem"
                @click="pushLive"
              >
                Announced
              </button>
            </div>
          </article>

          <article class="panel state-card">
            <div class="state-card-header">
              <div>
                <p class="state-label">Last Announced</p>
                <h2 class="panel-title">Lower Thirds</h2>
              </div>
              <span v-if="visibleItem" class="status-pill visible">Locked</span>
            </div>

            <div v-if="visibleItem" class="state-body">
              <p class="state-title">{{ itemHeadline(visibleItem) }}</p>
              <p v-if="itemSubline(visibleItem)" class="state-subline">{{ itemSubline(visibleItem) }}</p>
              <p v-if="itemPhonetics(visibleItem)" class="queue-phonetics">Orator notes / phonetics: {{ itemPhonetics(visibleItem) }}</p>
            </div>
            <div v-else class="empty-state">
              Nothing has been announced to lower thirds yet.
            </div>
          </article>
        </section>

        <section class="panel mb-6">
          <div class="controls-row">
            <div>
              <h2 class="panel-title">Upcoming Queue</h2>
              <p class="panel-copy">Only upcoming items can be reordered. Last announced and preview stay pinned at the front.</p>
            </div>
            <div class="header-actions">
              <button
                class="secondary-button"
                type="button"
                :aria-pressed="keepQueuePlace"
                aria-label="Keep my place"
                title="Keep the same row in view during live updates. You can still scroll manually."
                @click="keepQueuePlace = !keepQueuePlace"
              >
                Keep my place: {{ keepQueuePlace ? "On" : "Off" }}
              </button>
              <button class="secondary-button" type="button" @click="activeQueueDrawer = 'manual'">
                Add Manual
              </button>
              <button class="secondary-button" type="button" @click="activeQueueDrawer = 'search'">
                Search
              </button>
            </div>
          </div>

          <div
            ref="upcomingVirtualScroller"
            class="mt-6 queue-viewport"
            :class="{ 'virtual-queue-list': useVirtualUpcomingQueue }"
            @scroll="onUpcomingVirtualScroll"
            @wheel.passive="queueScrollRestoreVersion++"
            @touchstart.passive="queueScrollRestoreVersion++"
            @pointerdown="queueScrollRestoreVersion++"
            @keydown="queueScrollRestoreVersion++"
          >
            <div v-if="!upcomingItems.length" class="empty-state compact">
              No upcoming items remain in the queue.
            </div>
            <div
              v-else-if="useVirtualUpcomingQueue"
              class="virtual-queue-spacer"
              :style="{ height: `${virtualUpcomingTotalHeight}px` }"
            >
              <div
                class="virtual-queue-window"
                :style="{ transform: `translateY(${virtualUpcomingOffsetY}px)` }"
              >
                <div
                  v-for="row in virtualUpcomingRows"
                  :key="row.item.id"
                  :data-queue-item-id="row.item.id"
                  class="queue-card upcoming virtual-queue-card"
                >
                  <div class="queue-card-header">
                    <div class="queue-card-title">
                      <span>{{ upcomingPosition(row.index) }}. {{ row.item.type.replace("_", " ") }}</span>
                    </div>
                    <div class="queue-card-badges">
                      <span v-if="seatNumberPillText(row.item)" class="seat-number-pill">
                        {{ seatNumberPillText(row.item) }}
                      </span>
                      <span class="status-pill upcoming">Upcoming</span>
                    </div>
                  </div>

                  <div class="queue-copy">
                    <p class="queue-headline">{{ itemHeadline(row.item) }}</p>
                    <p v-if="itemSubline(row.item)" class="queue-subline">{{ itemSubline(row.item) }}</p>
                    <p v-if="itemPhonetics(row.item)" class="queue-phonetics">Orator notes / phonetics: {{ itemPhonetics(row.item) }}</p>
                  </div>

                  <div class="card-actions">
                    <button class="secondary-button compact-action-button" @click="openMoveModal(row.item.id)">
                      Move
                    </button>
                    <button class="secondary-button compact-action-button" @click="openSendToBackModal(row.item.id)">
                      Send to Back
                    </button>
                    <button class="danger-button compact-action-button" @click="openRemoveModal(row.item.id)">
                      Remove
                    </button>
                  </div>
                </div>
              </div>
            </div>

            <div v-else class="queue-list">
              <div v-for="(element, index) in upcomingItems" :key="element.id" class="queue-card upcoming" :data-queue-item-id="element.id">
                <div class="queue-card-header">
                  <div class="queue-card-title">
                    <span>{{ upcomingPosition(index) }}. {{ element.type.replace("_", " ") }}</span>
                  </div>
                  <div class="queue-card-badges">
                    <span v-if="seatNumberPillText(element)" class="seat-number-pill">
                      {{ seatNumberPillText(element) }}
                    </span>
                    <span class="status-pill upcoming">Upcoming</span>
                  </div>
                </div>

                <div class="queue-copy">
                  <p class="queue-headline">{{ itemHeadline(element) }}</p>
                  <p v-if="itemSubline(element)" class="queue-subline">{{ itemSubline(element) }}</p>
                  <p v-if="itemPhonetics(element)" class="queue-phonetics">Orator notes / phonetics: {{ itemPhonetics(element) }}</p>
                </div>

                <div class="card-actions">
                  <button class="secondary-button" @click="openMoveModal(element.id)">
                    Move
                  </button>
                  <button class="secondary-button" @click="openSendToBackModal(element.id)">
                    Send to Back
                  </button>
                  <button class="danger-button" @click="openRemoveModal(element.id)">
                    Remove
                  </button>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section class="panel">
          <div class="controls-row">
            <div>
              <h2 class="panel-title">Used</h2>
              <p class="panel-copy">Completed items move here when they are replaced on lower thirds.</p>
            </div>
          </div>

          <div v-if="!history.length" class="empty-state compact">
            No used items yet.
          </div>

          <div v-else class="used-list mt-6">
            <div v-for="entry in history" :key="entry.historyId" class="used-row">
              <div class="used-copy">
                <p class="used-headline">{{ itemHeadline(entry.item) }}</p>
                <p v-if="itemSubline(entry.item)" class="used-subline">{{ itemSubline(entry.item) }}</p>
                <p v-if="itemPhonetics(entry.item)" class="queue-phonetics">Orator notes / phonetics: {{ itemPhonetics(entry.item) }}</p>
              </div>
              <div class="used-meta">
                <span>{{ formatTimestamp(entry.usedAt) }}</span>
                <button class="secondary-button" @click="restoreItem(entry.historyId)">Restore</button>
              </div>
            </div>
          </div>
        </section>

        <button class="floating-top-button" type="button" @click="scrollToTop">
          Back to Top
        </button>

        <div
          v-if="activeQueueDrawer"
          class="drawer-overlay"
          @click.self="activeQueueDrawer = ''"
        >
          <aside class="search-drawer">
            <div class="drawer-header">
              <div>
                <h2 class="panel-title">{{ activeQueueDrawer === 'manual' ? 'Manual Queue Item' : 'Queue Search' }}</h2>
                <p class="panel-copy">
                  {{ activeQueueDrawer === 'manual'
                    ? 'Use this for show-only rows or if the late student cannot be found in the roster search.'
                    : 'Search across last announced, preview, and upcoming items. Send matching upcoming rows to the back.' }}
                </p>
              </div>
              <button class="secondary-button" type="button" @click="activeQueueDrawer = ''">
                Close
              </button>
            </div>

            <div v-if="activeQueueDrawer === 'search'" class="mt-4">
              <input
                v-model.trim="queueSearchTerm"
                type="text"
                class="field-input"
                placeholder="Search the active queue..."
              />
            </div>

            <div v-if="activeQueueDrawer === 'search' && queueSearchTerm" class="search-results mt-6">
              <div v-if="!queueSearchResults.length" class="empty-state compact">
                No queue items match this search.
              </div>

              <div v-else class="queue-list">
                <div
                  v-for="result in queueSearchResults"
                  :key="`search-${result.item.id}`"
                  class="queue-card"
                  :class="statusClass(result.role)"
                >
                  <div class="queue-card-header">
                    <div class="queue-card-title">
                      <span>{{ result.position }}. {{ result.item.type.replace("_", " ") }}</span>
                    </div>
                    <div class="queue-card-badges">
                      <span v-if="seatNumberPillText(result.item)" class="seat-number-pill">
                        {{ seatNumberPillText(result.item) }}
                      </span>
                      <span class="status-pill" :class="statusClass(result.role)">{{ statusLabel(result.role) }}</span>
                    </div>
                  </div>

                  <div class="queue-copy">
                    <p class="queue-headline">{{ itemHeadline(result.item) }}</p>
                    <p v-if="itemSubline(result.item)" class="queue-subline">{{ itemSubline(result.item) }}</p>
                    <p v-if="itemPhonetics(result.item)" class="queue-phonetics">Orator notes / phonetics: {{ itemPhonetics(result.item) }}</p>
                  </div>

                  <div class="card-actions">
                    <button
                      v-if="result.role === 'upcoming'"
                      class="secondary-button"
                      @click="openMoveModal(result.item.id)"
                    >
                      Move
                    </button>
                    <button
                      v-if="result.role === 'upcoming'"
                      class="secondary-button"
                      @click="openSendToBackModal(result.item.id)"
                    >
                      Send to Back
                    </button>
                  </div>
                </div>
              </div>
            </div>

            <div v-if="activeQueueDrawer === 'manual'" class="mt-4">
              <div class="grid gap-4 md:grid-cols-4">
                <div>
                  <label class="field-label">Type</label>
                  <select v-model="newItem.type" class="field-input">
                    <option value="student">student</option>
                    <option value="presenter_note">presenter_note</option>
                    <option value="display_line">display_line</option>
                  </select>
                </div>
                <div v-if="newItem.type === 'student'">
                  <label class="field-label">Full Name</label>
                  <input v-model="newItem.fullName" type="text" class="field-input" />
                </div>
                <div v-if="newItem.type === 'student'">
                  <label class="field-label">Award</label>
                  <input v-model="newItem.award" type="text" class="field-input" />
                </div>
                <div v-if="newItem.type !== 'student'" class="md:col-span-3">
                  <label class="field-label">Text</label>
                  <input v-model="newItem.text" type="text" class="field-input" />
                </div>
              </div>

              <div class="mt-4">
                <button class="primary-button" @click="addItem">
                  Add Item
                </button>
              </div>
            </div>
          </aside>
        </div>

        <div
          v-if="moveItem"
          class="modal-overlay"
          @click.self="closeMoveModal"
        >
          <div class="confirm-modal">
            <div class="confirm-modal-header">
              <div>
                <h2 class="panel-title">Move Upcoming Item?</h2>
                <p class="panel-copy">
                  Choose where to move <strong>{{ itemHeadline(moveItem) }}</strong> in the upcoming queue.
                  Last Announced and Preview will not change.
                </p>
              </div>
              <button class="secondary-button modal-close-button" type="button" @click="closeMoveModal">
                Close
              </button>
            </div>

            <div class="card-actions modal-actions move-actions">
              <button class="secondary-button" type="button" :disabled="!canMoveItemUp" @click="confirmMove('up')">
                Move Up
              </button>
              <button class="secondary-button" type="button" :disabled="!canMoveItemDown" @click="confirmMove('down')">
                Move Down
              </button>
              <button class="secondary-button" type="button" :disabled="!canMoveItemUp" @click="confirmMove('top')">
                Move to Top
              </button>
              <button class="secondary-button" type="button" @click="closeMoveModal">
                Cancel
              </button>
            </div>
          </div>
        </div>

        <div
          v-if="sendToBackItem"
          class="modal-overlay"
          @click.self="closeSendToBackModal"
        >
          <div class="confirm-modal">
            <div class="confirm-modal-header">
              <div>
                <h2 class="panel-title">Send to Back of Upcoming?</h2>
                <p class="panel-copy">
                  This will move <strong>{{ itemHeadline(sendToBackItem) }}</strong> to the end of the upcoming queue.
                  Last Announced and Preview will not change.
                </p>
              </div>
              <button class="secondary-button modal-close-button" type="button" @click="closeSendToBackModal">
                Close
              </button>
            </div>

            <div class="card-actions modal-actions">
              <button class="secondary-button" type="button" @click="closeSendToBackModal">
                Cancel
              </button>
              <button class="primary-button" type="button" :disabled="!canSendToBackItem" @click="confirmSendToBack">
                Send to Back
              </button>
            </div>
          </div>
        </div>

        <div
          v-if="removeItem"
          class="modal-overlay"
          @click.self="closeRemoveModal"
        >
          <div class="confirm-modal confirm-modal-danger">
            <div class="confirm-modal-header">
              <div>
                <h2 class="panel-title">Remove From Upcoming?</h2>
                <p class="panel-copy confirm-copy-danger">
                  This will remove <strong>{{ itemHeadline(removeItem) }}</strong> from the upcoming queue.
                  This change is immediate after save and does not affect Last Announced or Preview.
                </p>
              </div>
              <button class="secondary-button modal-close-button" type="button" @click="closeRemoveModal">
                Close
              </button>
            </div>

            <div class="card-actions modal-actions">
              <button class="secondary-button" type="button" @click="closeRemoveModal">
                Cancel
              </button>
              <button class="danger-button" type="button" :disabled="!canOperate" @click="confirmRemove">
                Remove
              </button>
            </div>
          </div>
        </div>
      </template>
      </fieldset>
    </main>
  </div>
</template>

<script>
import {
  addParticipantToQueue,
  pushPreviewLive,
  restoreHistoryItem,
  undoPushPreviewLive,
  updateQueue,
} from "@/lib/api";
import queueSyncMixin from "@/lib/queueSyncMixin";
import { editQueue } from "@/lib/queueEdits";
import { captureQueueAnchor, resolveQueueAnchor, captureQueuePageAnchor, restoreQueuePageAnchor } from "@/lib/queueScroll";
import { cloneViews, DEFAULT_VIEWS, resolveDisplayValue } from "@/lib/viewConfig";
import EventSidebar from "./EventSidebar.vue";

const VIRTUAL_QUEUE_THRESHOLD = 150;
const VIRTUAL_QUEUE_ROW_HEIGHT = 204;
const VIRTUAL_QUEUE_BUFFER_ROWS = 8;

export default {
  name: "ManagementView",
  mixins: [queueSyncMixin],
  components: {
    EventSidebar,
  },
  data() {
    return {
      snapshot: null,
      localItems: [],
      visibleIndex: null,
      previewIndex: null,
      history: [],
      queueSearchTerm: "",
      activeQueueDrawer: "",
      lateAddSearchTerm: "",
      moveItemId: "",
      sendToBackItemId: "",
      removeItemId: "",
      addingParticipantId: "",
      keepQueuePlace: true,
      queueScrollRestoreVersion: 0,
      queueScrollMovedItemIds: [],
      queuePageAnchor: null,
      upcomingVirtualScrollTop: 0,
      upcomingVirtualViewportHeight: 640,
      error: "",
      successMessage: "",
      newItem: {
        type: "student",
        fullName: "",
        award: "",
        text: "",
      },
    };
  },
  computed: {
    eventId() {
      return this.$route.params.eventId;
    },
    eventName() {
      return this.snapshot?.event?.name || this.eventId;
    },
    queueViewConfig() {
      return cloneViews(this.snapshot?.views || DEFAULT_VIEWS, this.snapshot?.event).queue;
    },
    queuePublishedAt() {
      return this.snapshot?.queue?.publishedAt || "";
    },
    formattedPublishedAt() {
      return this.formatTimestamp(this.queuePublishedAt);
    },
    visibleItem() {
      return this.itemAt(this.visibleIndex);
    },
    previewItem() {
      return this.itemAt(this.previewIndex);
    },
    upcomingItems() {
      return this.localItems.filter((item, index) => !this.isPinnedIndex(index));
    },
    useVirtualUpcomingQueue() {
      return this.upcomingItems.length > VIRTUAL_QUEUE_THRESHOLD;
    },
    virtualUpcomingTotalHeight() {
      return this.upcomingItems.length * VIRTUAL_QUEUE_ROW_HEIGHT;
    },
    virtualUpcomingStartIndex() {
      const startIndex = Math.floor(this.upcomingVirtualScrollTop / VIRTUAL_QUEUE_ROW_HEIGHT);
      return Math.max(startIndex - VIRTUAL_QUEUE_BUFFER_ROWS, 0);
    },
    virtualUpcomingEndIndex() {
      const visibleRows = Math.ceil(this.upcomingVirtualViewportHeight / VIRTUAL_QUEUE_ROW_HEIGHT);
      return Math.min(
        this.virtualUpcomingStartIndex + visibleRows + VIRTUAL_QUEUE_BUFFER_ROWS * 2,
        this.upcomingItems.length
      );
    },
    virtualUpcomingOffsetY() {
      return this.virtualUpcomingStartIndex * VIRTUAL_QUEUE_ROW_HEIGHT;
    },
    virtualUpcomingRows() {
      return this.upcomingItems
        .slice(this.virtualUpcomingStartIndex, this.virtualUpcomingEndIndex)
        .map((item, offset) => ({
          item,
          index: this.virtualUpcomingStartIndex + offset,
        }));
    },
    queueSearchResults() {
      const query = this.queueSearchTerm.trim().toLowerCase();
      if (!query) {
        return [];
      }
      return this.localItems
        .map((item, index) => ({
          item,
          index,
          position: index + 1,
          role: this.roleForIndex(index),
        }))
        .filter(({ item, role }) => this.matchesQueueSearch(item, role, query));
    },
    lateAddSearchResults() {
      const query = this.lateAddSearchTerm.trim().toLowerCase();
      if (!query) {
        return [];
      }
      return (this.snapshot?.participants?.items || [])
        .filter((item) => item.type === "student")
        .filter((item) => this.matchesLateAddSearch(item, query))
        .map((participant) => {
          const duplicateContext = this.lateAddDuplicateContext(participant.id);
          return {
            participant,
            duplicateContext,
            isDuplicate: duplicateContext.inActiveQueue || duplicateContext.inHistory,
          };
        });
    },
    moveItem() {
      if (!this.moveItemId) {
        return null;
      }
      return this.upcomingItems.find((item) => item.id === this.moveItemId) || null;
    },
    moveItemIndex() {
      if (!this.moveItemId) {
        return -1;
      }
      return this.upcomingItems.findIndex((item) => item.id === this.moveItemId);
    },
    canMoveItemUp() {
      return this.moveItemIndex > 0;
    },
    canMoveItemDown() {
      const index = this.moveItemIndex;
      return index >= 0 && index < this.upcomingItems.length - 1;
    },
    sendToBackItem() {
      if (!this.sendToBackItemId) {
        return null;
      }
      return this.upcomingItems.find((item) => item.id === this.sendToBackItemId) || null;
    },
    sendToBackItemIndex() {
      if (!this.sendToBackItemId) {
        return -1;
      }
      return this.upcomingItems.findIndex((item) => item.id === this.sendToBackItemId);
    },
    canSendToBackItem() {
      const index = this.sendToBackItemIndex;
      return index >= 0 && index < this.upcomingItems.length - 1;
    },
    removeItem() {
      if (!this.removeItemId) {
        return null;
      }
      return this.upcomingItems.find((item) => item.id === this.removeItemId) || null;
    },
    canUndoAnnounce() {
      return this.visibleIndex !== null && this.visibleIndex !== undefined;
    },
    participantsPath() {
      return `/events/${encodeURIComponent(this.eventId)}/participants`;
    },
  },
  watch: {
    "syncState.pending"(pending) {
      // Keep scroll exclusions through reconciliation and any unconfirmed retry.
      if (!pending) this.$nextTick(() => {
        if (!this.syncState.pending) this.queueScrollMovedItemIds = [];
      });
    },
    async upcomingItems(nextItems, previousItems) {
      const version = ++this.queueScrollRestoreVersion;
      const movedItemIds = [...this.queueScrollMovedItemIds];
      if (!this.keepQueuePlace) {
        return;
      }
      // Watchers run before Vue patches the DOM: capture the row still on screen.
      const anchor = captureQueueAnchor(this.$refs.upcomingVirtualScroller, previousItems);
      if (!anchor) {
        return;
      }
      await this.$nextTick();
      await this.restoreQueueScroll(anchor, version, movedItemIds);
    },
  },
  methods: {
    itemAt(index) {
      if (index === null || index === undefined || index < 0) {
        return null;
      }
      return this.localItems[index] || null;
    },
    isPinnedIndex(index) {
      return index === this.visibleIndex || index === this.previewIndex;
    },
    roleForIndex(index) {
      if (index === this.visibleIndex) {
        return "visible";
      }
      if (index === this.previewIndex) {
        return "preview";
      }
      return "upcoming";
    },
    statusClass(role) {
      return role;
    },
    statusLabel(role) {
      if (role === "visible") {
        return "Last Announced";
      }
      if (role === "preview") {
        return "Preview";
      }
      return "Upcoming";
    },
    itemHeadline(item) {
      if (!item) {
        return "";
      }
      return item.type === "student" ? item.fullName : item.text;
    },
    participantDisplayName(participant) {
      const fullName = (participant?.fullName || "").trim();
      if (fullName) {
        return fullName;
      }
      const firstName = this.participantExtraFieldValue(participant, ["firstname", "first_name"]);
      const lastName = this.participantExtraFieldValue(participant, ["lastname", "last_name"]);
      return [firstName, lastName].filter(Boolean).join(" ").trim();
    },
    itemSubline(item) {
      if (!item || item.type !== "student") {
        return "";
      }
      return item.award || "";
    },
    itemPhonetics(item) {
      if (!item || item.type !== "student") {
        return "";
      }
      return `${resolveDisplayValue(item, "presenterPhonetics")}`.trim();
    },
    seatNumberPillText(item) {
      const pill = this.queueViewConfig.seatNumberPill;
      if (!pill.enabled || !pill.field || item?.type !== "student") {
        return "";
      }
      const value = `${resolveDisplayValue(item, pill.field) || ""}`.trim();
      if (!value) {
        return "";
      }
      return [`${pill.label || ""}`.trim(), value].filter(Boolean).join(" ");
    },
    formatTimestamp(value) {
      if (!value) {
        return "Just now";
      }
      const date = new Date(value);
      if (Number.isNaN(date.getTime())) {
        return value;
      }
      return date.toLocaleString();
    },
    matchesQueueSearch(item, role, query) {
      const haystack = [
        item.type,
        item.fullName,
        item.award,
        item.text,
        role,
        ...Object.values(item.extraFields || {}),
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return haystack.includes(query);
    },
    participantExtraFieldValue(participant, keys) {
      const extraFields = participant?.extraFields || {};
      const normalizedKeys = Array.isArray(keys) ? keys.map((key) => key.toLowerCase()) : [];
      for (const [key, value] of Object.entries(extraFields)) {
        if (normalizedKeys.includes(key.toLowerCase()) && value) {
          return `${value}`.trim();
        }
      }
      return "";
    },
    matchesLateAddSearch(participant, query) {
      return this.participantDisplayName(participant).toLowerCase().includes(query);
    },
    lateAddDuplicateContext(participantId) {
      return {
        inActiveQueue: this.localItems.some((item) => item.sourceParticipantId === participantId),
        inHistory: this.history.some((entry) => (entry.item || {}).sourceParticipantId === participantId),
      };
    },
    lateAddStatusLabel(duplicateContext) {
      if (duplicateContext.inActiveQueue && duplicateContext.inHistory) {
        return "Already in queue + used";
      }
      if (duplicateContext.inActiveQueue) {
        return "Already in queue";
      }
      if (duplicateContext.inHistory) {
        return "Already used";
      }
      return "Not in queue";
    },
    lateAddWarningCopy(duplicateContext) {
      if (duplicateContext.inActiveQueue && duplicateContext.inHistory) {
        return "This student is already in the active queue and used history. Add Again to append another copy to the bottom of upcoming.";
      }
      if (duplicateContext.inActiveQueue) {
        return "This student is already in the active queue. Add Again to append another copy to the bottom of upcoming.";
      }
      return "This student is already in used history. Add Again to append another copy to the bottom of upcoming.";
    },
    lateAddActionLabel(duplicateContext) {
      return duplicateContext.inActiveQueue || duplicateContext.inHistory ? "Add Again" : "Add to Bottom";
    },
    upcomingPosition(index) {
      const offset = (this.visibleItem ? 1 : 0) + (this.previewItem ? 1 : 0);
      return offset + index + 1;
    },
    applySnapshot(snapshot) {
      if (this.snapshot && this.queueRevisionFor(snapshot.queue) < this.currentQueueRevision()) return;
      this.snapshot = snapshot;
      this.applyQueueState(snapshot.queue || {});
    },
    applyQueueState(queue) {
      this.localItems = (queue?.items || []).map((item) => ({ ...item }));
      this.visibleIndex =
        queue?.visibleIndex === null || queue?.visibleIndex === undefined
          ? null
          : queue.visibleIndex;
      this.previewIndex =
        queue?.previewIndex === null || queue?.previewIndex === undefined
          ? null
          : queue.previewIndex;
      this.history = (queue?.history || []).map((entry) => ({
        ...entry,
        item: { ...(entry.item || {}) },
      }));
      if (this.snapshot) {
        this.snapshot = {
          ...this.snapshot,
          queue: { ...queue },
        };
      }
      this.$nextTick(() => this.updateUpcomingVirtualViewport());
    },
    queueRevisionFor(queueLike) {
      const revision = Number.parseInt(queueLike?.revision || queueLike?.queueRevision || 0, 10);
      return Number.isNaN(revision) ? 0 : revision;
    },
    currentQueueRevision() {
      return this.queueRevisionFor(this.snapshot?.queue);
    },
    async pushLive() {
      await this.runQueueOperation(payload => pushPreviewLive(this.eventId, payload));
    },
    async undoLastAnnounce() {
      await this.runQueueOperation(payload => undoPushPreviewLive(this.eventId, payload));
    },
    openMoveModal(itemId) {
      this.moveItemId = itemId;
    },
    closeMoveModal() {
      this.moveItemId = "";
    },
    async confirmMove(action) {
      if (!this.moveItem) {
        this.closeMoveModal();
        return;
      }
      await this.moveUpcomingItem(this.moveItem.id, action);
    },
    async moveUpcomingItem(itemId, action) {
      await this.runQueueOperation(payload => updateQueue(this.eventId, payload), queue => {
        const draft = editQueue(queue, "move", { itemId, position: action });
        this.queueScrollMovedItemIds = [itemId];
        return draft;
      });
      this.closeMoveModal();
    },
    moveSuccessMessage(action) {
      const labels = {
        up: "Item moved up.",
        down: "Item moved down.",
        top: "Item moved to the top of the upcoming queue.",
        bottom: "Item moved to the bottom of the upcoming queue.",
      };
      return labels[action] || "Queue updated.";
    },
    openSendToBackModal(itemId) {
      this.sendToBackItemId = itemId;
    },
    closeSendToBackModal() {
      this.sendToBackItemId = "";
    },
    async confirmSendToBack() {
      if (!this.sendToBackItem) {
        this.closeSendToBackModal();
        return;
      }
      await this.sendToBack(this.sendToBackItem.id);
    },
    async sendToBack(itemId) {
      await this.runQueueOperation(payload => updateQueue(this.eventId, payload), queue => {
        const draft = editQueue(queue, "move", { itemId, position: "bottom" });
        this.queueScrollMovedItemIds = [itemId];
        return draft;
      });
      this.closeSendToBackModal();
    },
    openRemoveModal(itemId) {
      this.removeItemId = itemId;
    },
    closeRemoveModal() {
      this.removeItemId = "";
    },
    async confirmRemove() {
      if (!this.removeItem) {
        this.closeRemoveModal();
        return;
      }
      await this.removeUpcomingItem(this.removeItem.id);
    },
    scrollToTop() {
      window.scrollTo({
        top: 0,
        behavior: "smooth",
      });
    },
    async restoreQueueScroll(anchor, version, movedItemIds) {
      const scroller = this.$refs.upcomingVirtualScroller;
      const target = resolveQueueAnchor(anchor, this.upcomingItems, movedItemIds);
      if (!scroller || !target || !this.keepQueuePlace || version !== this.queueScrollRestoreVersion) {
        return;
      }
      if (this.useVirtualUpcomingQueue) {
        // Bring the anchor into the rendered window before measuring its exact offset.
        scroller.scrollTop = target.index * VIRTUAL_QUEUE_ROW_HEIGHT;
        this.upcomingVirtualScrollTop = scroller.scrollTop;
        await this.$nextTick();
      }
      if (!this.keepQueuePlace || version !== this.queueScrollRestoreVersion) {
        return;
      }
      const row = Array.from(scroller.querySelectorAll("[data-queue-item-id]"))
        .find((element) => element.dataset.queueItemId === target.id);
      if (row) {
        const viewportTop = scroller.getBoundingClientRect().top + scroller.clientTop;
        scroller.scrollTop += row.getBoundingClientRect().top - viewportTop - target.offset;
      }
      this.updateUpcomingVirtualViewport();
    },
    onUpcomingVirtualScroll(event) {
      this.upcomingVirtualScrollTop = event.target.scrollTop;
      this.updateUpcomingVirtualViewport();
    },
    updateUpcomingVirtualViewport() {
      const scroller = this.$refs.upcomingVirtualScroller;
      if (!scroller) {
        return;
      }
      this.upcomingVirtualViewportHeight = scroller.clientHeight || this.upcomingVirtualViewportHeight;
      this.upcomingVirtualScrollTop = scroller.scrollTop || 0;
    },
    async restoreItem(historyId) {
      await this.runQueueOperation(payload => restoreHistoryItem(this.eventId, historyId, payload));
    },
    async addRosterParticipant(participantId, allowDuplicate = false) {
      const saved = await this.runQueueOperation(payload => addParticipantToQueue(this.eventId, participantId, allowDuplicate, payload));
      if (saved) this.lateAddSearchTerm = "";
    },
    async removeUpcomingItem(itemId) {
      await this.runQueueOperation(payload => updateQueue(this.eventId, payload), queue => editQueue(queue, "remove", { itemId }));
      this.closeRemoveModal();
    },
    async addItem() {
      const item = {
        id: `queue-${Date.now()}-${Math.random().toString(36).slice(2)}`,
        type: this.newItem.type,
        extraFields: {},
      };

      if (item.type === "student") {
        item.fullName = this.newItem.fullName.trim();
        item.award = this.newItem.award.trim();
        if (!item.fullName || !item.award) {
          this.error = "Student items need both a full name and an award.";
          return;
        }
      } else {
        item.text = this.newItem.text.trim();
        if (!item.text) {
          this.error = "Text is required for presenter notes and display lines.";
          return;
        }
      }

      const saved = await this.runQueueOperation(payload => updateQueue(this.eventId, payload), queue => editQueue(queue, "add", { item }));
      if (!saved) return;

      this.newItem = {
        type: "student",
        fullName: "",
        award: "",
        text: "",
      };
      this.activeQueueDrawer = "";
    },
  },
  beforeUpdate() {
    this.queuePageAnchor = this.keepQueuePlace
      ? captureQueuePageAnchor(this.$refs.upcomingVirtualScroller, window)
      : null;
  },
  updated() {
    const anchor = this.queuePageAnchor;
    this.queuePageAnchor = null;
    if (this.keepQueuePlace) {
      restoreQueuePageAnchor(this.$refs.upcomingVirtualScroller, window, anchor);
    }
  },
  async mounted() {
    this.startQueueSync();
    window.addEventListener("resize", this.updateUpcomingVirtualViewport);
    this.$nextTick(() => this.updateUpcomingVirtualViewport());
  },
  beforeUnmount() {
    window.removeEventListener("resize", this.updateUpcomingVirtualViewport);
  },
};
</script>

<style scoped>
.queue-operation-controls { border: 0; padding: 0; margin: 1rem 0 0; min-width: 0; }
.queue-operation-controls:disabled button { opacity: 0.5; cursor: not-allowed; }
.event-layout {
  display: flex;
  gap: 1rem;
  width: calc(100vw - 0.25rem);
  max-width: none;
  box-sizing: border-box;
  margin: 0 auto;
  padding: 0 0.125rem 2.5rem;
}

.event-main {
  flex: 1 1 auto;
  min-width: 0;
}

:deep(.sidebar) {
  width: 168px;
  flex-basis: 168px;
}

.page-header,
.controls-row,
.header-actions,
.card-actions,
.queue-card-header,
.state-card-header,
.used-row {
  display: flex;
  justify-content: space-between;
  gap: 1rem;
  align-items: flex-start;
  flex-wrap: wrap;
}

.breadcrumb {
  font-size: 0.9rem;
  font-weight: 600;
  color: var(--orator-graphite);
}

.page-title {
  margin-top: 0.35rem;
  font-size: 2rem;
  font-weight: 900;
  color: var(--orator-navy);
}

.page-subtitle,
.panel-copy,
.published-copy {
  margin-top: 0.35rem;
  color: var(--orator-graphite);
  font-weight: 500;
}

.panel {
  background: white;
  border: 1px solid var(--orator-smoke);
  border-radius: 0.75rem;
  box-shadow: 0 1px 3px 0 rgb(0 0 0 / 0.06);
  padding: 1.5rem;
}

.panel-title {
  font-size: 1.35rem;
  font-weight: 900;
  color: var(--orator-navy);
}

.state-grid {
  display: grid;
  gap: 1.5rem;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
}

.state-card {
  min-height: 18rem;
  display: flex;
  flex-direction: column;
}

.state-label {
  font-size: 0.8rem;
  font-weight: 800;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--orator-graphite);
}

.state-body,
.queue-copy,
.used-copy {
  margin-top: 1rem;
}

.state-title,
.queue-headline,
.used-headline {
  font-size: 1.5rem;
  line-height: 1.1;
  font-weight: 900;
  color: var(--orator-navy);
}

.state-subline,
.queue-subline,
.used-subline {
  margin-top: 0.5rem;
  font-size: 1rem;
  color: var(--orator-graphite);
}

.queue-phonetics {
  margin-top: 0.35rem;
  font-size: 0.95rem;
  font-weight: 800;
  color: var(--orator-navy);
}

.queue-headline {
  font-size: 1.12rem;
}

.queue-subline {
  margin-top: 0.25rem;
  font-size: 0.88rem;
}

.state-meta,
.queue-meta {
  margin-top: 0.75rem;
  color: var(--orator-graphite);
  font-weight: 500;
}

.queue-list {
  display: grid;
  gap: 0.7rem;
}

.queue-viewport {
  max-height: min(68vh, 720px);
  overflow: auto;
  overflow-anchor: none;
  overscroll-behavior: contain;
  border: 1px solid var(--orator-smoke);
  border-radius: 0.75rem;
  background: var(--orator-cloud);
}

.queue-viewport > .queue-list {
  margin-top: 0;
  padding: 0.7rem;
}

.virtual-queue-list {
  height: min(68vh, 720px);
  min-height: 28rem;
  contain: strict;
}

.virtual-queue-spacer {
  position: relative;
}

.virtual-queue-window {
  position: absolute;
  inset: 0 0 auto;
  display: grid;
  gap: 0.7rem;
  padding: 0.7rem;
  will-change: transform;
}

.virtual-queue-card {
  height: calc(var(--virtual-queue-row-height, 204px) - 0.7rem);
  min-height: 11.8rem;
  overflow: hidden;
}

.virtual-queue-card .compact-action-button {
  min-height: 2.45rem;
  padding: 0.55rem 1rem;
  font-size: 0.9rem;
}

.late-add-list {
  display: grid;
  gap: 0.85rem;
}

.queue-card {
  border: 1px solid var(--orator-smoke);
  border-radius: 0.75rem;
  padding: 0.75rem 0.9rem;
  background: white;
}

.late-add-card {
  border: 1px solid var(--orator-smoke);
  border-radius: 0.75rem;
  padding: 0.9rem;
  background: white;
}

.queue-card.visible {
  border-color: #1d4ed8;
  box-shadow: inset 0 0 0 1px rgba(29, 78, 216, 0.12);
}

.queue-card.preview {
  border-color: #7c3aed;
  box-shadow: inset 0 0 0 1px rgba(124, 58, 237, 0.12);
}

.queue-card.upcoming {
  border-color: var(--orator-smoke);
}

.queue-card-title {
  display: flex;
  align-items: center;
  gap: 0.55rem;
  font-weight: 700;
  color: var(--orator-navy);
  font-size: 0.92rem;
}

.queue-card-badges {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  flex-wrap: wrap;
  gap: 0.45rem;
}

.seat-number-pill {
  display: inline-flex;
  align-items: center;
  border: 1px solid #d6ad00;
  border-radius: 999px;
  padding: 0.2rem 0.7rem;
  background: #fff7cc;
  color: #473800;
  font-size: 0.78rem;
  font-variant-numeric: tabular-nums;
  font-weight: 900;
  white-space: nowrap;
}

.drag-handle {
  border: none;
  background: transparent;
  color: var(--orator-graphite);
  font-weight: 900;
  cursor: grab;
  min-width: 2.75rem;
  min-height: 2.75rem;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 999px;
  font-size: 1rem;
}

.status-pill {
  display: inline-flex;
  align-items: center;
  border-radius: 999px;
  padding: 0.2rem 0.7rem;
  font-size: 0.78rem;
  font-weight: 800;
}

.status-pill.visible {
  background: #dbeafe;
  color: #1d4ed8;
}

.status-pill.preview {
  background: #ede9fe;
  color: #6d28d9;
}

.status-pill.upcoming {
  background: var(--orator-cloud);
  color: var(--orator-graphite);
}

.late-add-status {
  display: inline-flex;
  align-items: center;
  border-radius: 999px;
  padding: 0.2rem 0.7rem;
  font-size: 0.78rem;
  font-weight: 800;
  white-space: nowrap;
}

.late-add-status.clean {
  background: #ecfdf5;
  color: #047857;
}

.late-add-status.warning {
  background: #fffbeb;
  color: #b45309;
}

.field-label {
  display: block;
  font-size: 0.82rem;
  font-weight: 800;
  color: var(--orator-arsenic);
  margin-bottom: 0.35rem;
}

.field-input {
  width: 100%;
  border: 1px solid var(--orator-steel);
  border-radius: 0.75rem;
  padding: 0.8rem 1rem;
  font-size: 1rem;
  color: var(--orator-navy);
  background: white;
}

.field-input:focus {
  outline: 2px solid rgb(36 123 255 / 0.18);
  border-color: var(--orator-blue);
}

.primary-button,
.secondary-button,
.danger-button,
.primary-link {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 999px;
  min-height: 3rem;
  padding: 0.85rem 1.25rem;
  font-weight: 800;
  font-size: 0.98rem;
  text-decoration: none;
  transition: 160ms ease;
  border: 1px solid transparent;
  touch-action: manipulation;
}

.primary-button,
.primary-link {
  background: var(--orator-blue);
  color: white;
}

.secondary-button {
  background: white;
  color: var(--orator-graphite);
  border-color: var(--orator-steel);
}

.danger-button {
  background: white;
  color: #b91c1c;
  border-color: #fecaca;
}

.primary-button:disabled,
.secondary-button:disabled,
.danger-button:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.card-actions {
  margin-top: 0.65rem;
  gap: 0.85rem;
}

.state-actions {
  margin-top: auto;
  padding-top: 1rem;
  justify-content: flex-start;
  align-items: center;
}

.used-list {
  display: grid;
  gap: 0.75rem;
}

.late-add-warning {
  margin-top: 0.8rem;
  color: #92400e;
  font-weight: 600;
}

.used-row {
  padding: 0.85rem 1rem;
  border-radius: 0.75rem;
  background: var(--orator-cloud);
  border: 1px solid var(--orator-smoke);
  align-items: center;
}

.used-meta {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  color: var(--orator-graphite);
  font-size: 0.95rem;
}

.notice {
  margin: 1rem 0;
  border-radius: 0.75rem;
  padding: 0.9rem 1rem;
  font-weight: 700;
}

.notice.error {
  background: #fef2f2;
  color: #b91c1c;
  border: 1px solid #fecaca;
}

.notice.success {
  background: #ecfdf5;
  color: #047857;
  border: 1px solid #a7f3d0;
}

.empty-state {
  min-height: 10rem;
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  color: var(--orator-graphite);
  font-size: 1rem;
}

.empty-state.compact {
  min-height: 4rem;
}

.empty-panel {
  text-align: center;
}

.floating-top-button {
  position: fixed;
  right: 1.25rem;
  bottom: 1.25rem;
  z-index: 30;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: 3.25rem;
  padding: 0.95rem 1.2rem;
  border: 1px solid var(--orator-steel);
  border-radius: 999px;
  background: var(--orator-navy);
  color: white;
  font-size: 0.96rem;
  font-weight: 800;
  box-shadow: 0 12px 24px rgba(17, 24, 39, 0.18);
  touch-action: manipulation;
}

.search-results {
  border-top: 1px solid var(--orator-smoke);
  padding-top: 1.5rem;
}

.drawer-overlay {
  position: fixed;
  inset: 0;
  background: rgba(17, 24, 39, 0.38);
  z-index: 40;
  display: flex;
  justify-content: flex-end;
}

.search-drawer {
  width: min(38rem, 100vw);
  height: 100vh;
  background: white;
  border-left: 1px solid var(--orator-smoke);
  box-shadow: -12px 0 30px rgba(15, 23, 42, 0.12);
  padding: 1.5rem;
  overflow-y: auto;
}

.drawer-header {
  display: flex;
  justify-content: space-between;
  gap: 1rem;
  align-items: flex-start;
}

.search-drawer .search-results {
  border-top: none;
  padding-top: 0;
}

.modal-overlay {
  position: fixed;
  inset: 0;
  z-index: 50;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1.5rem;
  background: rgba(17, 24, 39, 0.52);
}

.confirm-modal {
  width: min(32rem, 100%);
  background: white;
  border: 1px solid var(--orator-smoke);
  border-radius: 1rem;
  box-shadow: 0 24px 60px rgba(15, 23, 42, 0.24);
  padding: 1.5rem;
}

.confirm-modal-danger {
  border-color: #fecaca;
  box-shadow: 0 24px 60px rgba(127, 29, 29, 0.18);
}

.confirm-modal-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
}

.confirm-copy-danger {
  color: #991b1b;
}

.modal-close-button {
  min-height: 2.75rem;
  padding: 0.7rem 1rem;
}

.modal-actions {
  display: flex;
  justify-content: flex-end;
}

.move-actions {
  justify-content: flex-start;
}

.mb-6 {
  margin-bottom: 1.5rem;
}

.mt-4 {
  margin-top: 1rem;
}

.mt-6 {
  margin-top: 1.5rem;
}

@media (max-width: 960px) {
  .event-layout {
    width: auto;
    flex-direction: column;
    padding: 0 1rem 2.5rem;
  }

  :deep(.sidebar) {
    width: 100%;
    flex-basis: 100%;
  }

  .floating-top-button {
    right: 1rem;
    bottom: 1rem;
  }

  .search-drawer {
    width: 100vw;
  }
}
</style>
